import {
  Minus,
  Plus,
  ShoppingBag,
  Trash2
} from "lucide-react";

import {
  Link
} from "react-router";

import {
  useCart
} from "../context/CartContext";

import { productImageUrl } from "../utils/product-image";

export function CartPage() {
  const {
    items,
    subtotal,
    removeItem,
    updateQuantity
  } =
    useCart();

  if (items.length === 0) {
    return (
      <main className="rini-section">
        <div className="rini-container text-center">
          <ShoppingBag
            size={42}
            className="mx-auto text-[#70845b]"
          />

          <h1 className="rini-heading mt-6 text-5xl">
            Your cart is empty
          </h1>

          <p className="mx-auto mt-4 max-w-xl leading-7 text-[#746d65]">
            Explore RiNi products and add
            your favourites to begin your
            order.
          </p>

          <Link
            to="/shop"
            className="rini-button-primary mt-8"
          >
            Continue shopping
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#fffdf8] py-8 sm:py-12 lg:py-20">
      <div className="rini-container">
        <div className="grid gap-12 lg:grid-cols-[1fr_360px]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              Your selection
            </p>

            <h1 className="rini-heading mt-3 text-4xl sm:text-5xl">
              Shopping Cart
            </h1>

            <div className="mt-10 space-y-5">
              {items.map((item) => {
                const primaryImage =
                  item.product.images?.find(
                    (image) =>
                      image.isPrimary
                  ) ??
                  item.product.images?.[0];

                return (
                  <article
                    key={item.product.id}
                    className="grid gap-4 rounded-[1.5rem] border border-[#5e3b2c]/10 bg-white p-4 sm:grid-cols-[140px_1fr] sm:gap-5 sm:rounded-[2rem] sm:p-5"
                  >
                    <div className="aspect-square overflow-hidden rounded-2xl bg-[#efe8de]">
                      {primaryImage ? (
                        <img
                          src={productImageUrl(
                            primaryImage.id
                          )}
                          alt={
                            item.product.name
                          }
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-[#746d65]">
                          RiNi
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#70845b]">
                            {
                              item.product.category.name
                            }
                          </p>

                          <Link
                            to={`/products/${item.product.slug}`}
                            className="mt-2 block font-serif text-2xl text-[#38251e]"
                          >
                            {
                              item.product.name
                            }
                          </Link>

                          <p className="mt-2 text-sm text-[#746d65]">
                            ₹
                            {Number(
                              item.product.price
                            ).toLocaleString(
                              "en-IN"
                            )}
                          </p>

                          {item.product.stockQuantity <=
                          0 ? (
                            <p className="mt-2 text-xs font-semibold text-red-700">
                              Out of stock
                            </p>
                          ) : item.product.stockQuantity <=
                            5 ? (
                            <p className="mt-2 text-xs font-semibold text-amber-700">
                              Only {
                                item.product
                                  .stockQuantity
                              } left
                            </p>
                          ) : (
                            <p className="mt-2 text-xs font-semibold text-[#526746]">
                              In stock
                            </p>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            removeItem(
                              item.product.id
                            )
                          }
                          className="self-start rounded-full p-2 text-[#8b5a49] transition hover:bg-[#f7e6e2]"
                          aria-label={`Remove ${item.product.name}`}
                        >
                          <Trash2
                            size={18}
                          />
                        </button>
                      </div>

                      <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center rounded-full border border-[#5e3b2c]/20">
                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(
                                item.product.id,
                                item.quantity -
                                  1
                              )
                            }
                            className="p-3"
                            aria-label="Decrease quantity"
                          >
                            <Minus
                              size={16}
                            />
                          </button>

                          <span className="min-w-10 text-center font-semibold">
                            {
                              item.quantity
                            }
                          </span>

                          <button
                            type="button"
                            disabled={
                              item.product
                                .stockQuantity <=
                                0 ||
                              item.quantity >=
                                item.product
                                  .stockQuantity
                            }
                            onClick={() =>
                              updateQuantity(
                                item.product.id,
                                item.quantity +
                                  1
                              )
                            }
                            className="p-3 disabled:cursor-not-allowed disabled:opacity-35"
                            aria-label="Increase quantity"
                          >
                            <Plus
                              size={16}
                            />
                          </button>
                        </div>

                        <div>
                          {item.product.stockQuantity >
                            0 &&
                            item.quantity >=
                              item.product
                                .stockQuantity && (
                              <p className="mb-2 text-right text-xs font-semibold text-amber-700">
                                Maximum available quantity reached
                              </p>
                            )}

                        <p className="font-semibold text-[#5e3b2c]">
                          ₹
                          {(
                            Number(
                              item.product.price
                            ) *
                            item.quantity
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </p>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <aside className="h-fit rounded-[2rem] bg-[#efe3d2] p-7 lg:sticky lg:top-28">
            <h2 className="font-serif text-2xl text-[#38251e]">
              Order summary
            </h2>

            <div className="mt-6 space-y-4 text-sm">
              <div className="flex justify-between">
                <span className="text-[#746d65]">
                  Subtotal
                </span>

                <span className="font-semibold text-[#38251e]">
                  ₹
                  {subtotal.toLocaleString(
                    "en-IN"
                  )}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-[#746d65]">
                  Shipping
                </span>

                <span className="font-semibold text-[#38251e]">
                  To be confirmed
                </span>
              </div>
            </div>

            <div className="mt-6 border-t border-[#5e3b2c]/10 pt-6">
              <div className="flex justify-between">
                <span className="font-serif text-xl text-[#38251e]">
                  Total
                </span>

                <span className="text-2xl font-semibold text-[#5e3b2c]">
                  ₹
                  {subtotal.toLocaleString(
                    "en-IN"
                  )}
                </span>
              </div>


              <p className="mt-3 text-xs leading-5 text-[#746d65]">
                Shipping will be confirmed by the RiNi team
                after reviewing your delivery location and package.
                Final payment details will be shared through WhatsApp.
              </p>

              <Link
                to="/checkout"
                className="rini-button-primary mt-6 min-h-13 w-full justify-center text-base"
              >
                Proceed to checkout
              </Link>

              <Link
                to="/shop"
                className="mt-3 block text-center text-sm font-medium text-[#5e3b2c]"
              >
                Continue shopping
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
